import apiClient from './client';
import { keysToCamelCase } from '@/lib/api-utils';
import type {
  CreditSummary,
  MyContexts,
  OrgItem,
  SwitchContextDto,
  SwitchContextResponse,
  UserSearchListResponse,
} from '@/types';
import type { CanvasShortcutOverrideMap } from '@/lib/canvas/canvas-shortcut-overrides';

export interface UserShortcutsPayload {
  canvas: CanvasShortcutOverrideMap;
}

export const usersApi = {
  async search(query: string, limit = 10): Promise<UserSearchListResponse> {
    const response = await apiClient.get('/users/search', {
      params: { query, limit },
    });
    return keysToCamelCase<UserSearchListResponse>(response);
  },

  /** 获取用户所属组织列表（上下文切换下拉用） */
  async listMyOrgs(): Promise<OrgItem[]> {
    return apiClient.get<OrgItem[]>('/users/me/orgs') as unknown as Promise<OrgItem[]>;
  },

  /** 获取全量主体列表 */
  async getMyContexts(): Promise<MyContexts> {
    return apiClient.get<MyContexts>('/users/me/contexts') as unknown as Promise<MyContexts>;
  },

  /** 切换激活上下文 */
  async switchContext(dto: SwitchContextDto): Promise<SwitchContextResponse> {
    const payload: Record<string, unknown> = { contextType: dto.contextType };
    if (dto.orgId) payload.orgId = dto.orgId;
    return apiClient.post<SwitchContextResponse>(
      '/users/me/switch-context',
      payload
    ) as unknown as Promise<SwitchContextResponse>;
  },

  /** 当前上下文词元汇总 */
  async getCreditSummary(): Promise<CreditSummary> {
    return apiClient.get<CreditSummary>(
      '/users/me/credit-summary'
    ) as unknown as Promise<CreditSummary>;
  },

  async putDefaultStoryboardSkill(
    skillId: string
  ): Promise<{ defaultSkillIds: Record<string, string> }> {
    return apiClient.put('/users/me/default-storyboard-skill', {
      skillId,
    }) as unknown as Promise<{ defaultSkillIds: Record<string, string> }>;
  },

  async removeDefaultStoryboardSkill(
    skillId: string
  ): Promise<{ defaultSkillIds: Record<string, string> }> {
    return apiClient.delete(`/users/me/default-storyboard-skill/${skillId}`) as unknown as Promise<{
      defaultSkillIds: Record<string, string>;
    }>;
  },

  async updateMyShortcuts(payload: UserShortcutsPayload): Promise<UserShortcutsPayload> {
    return apiClient.put<UserShortcutsPayload>('/users/me/shortcuts', {
      canvas: payload.canvas,
    }) as unknown as Promise<UserShortcutsPayload>;
  },
};
